import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const SiteContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || '';
// How often to re-pull site branding so admin changes show up "instantly" for
// all other users. Also re-pulled when the tab regains focus.
const POLL_INTERVAL_MS = 20000;
const POWERED_BY = 'Powered by Asset Logic';

export function SiteProvider({ children }) {
  const [site, setSite] = useState({
    site_name: 'Asset Logic',
    logo: '',
    favicon: '',
  });
  // Track current value in a ref so the poller can compare without re-triggering itself.
  const currentRef = useRef(site);

  const loadSettings = useCallback(() => {
    fetch(`${API_BASE}/api/admin/site-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        // Skip update when nothing changed — prevents re-renders, title flicker,
        // and clashes with in-progress edits in other UI.
        const prev = currentRef.current;
        const same = ['site_name', 'logo', 'favicon']
          .every(k => (prev[k] || '') === (data[k] || ''));
        if (same) return;
        currentRef.current = data;
        setSite(data);
      })
      .catch(() => {});
  }, []);

  // Initial load
  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Poll + refresh when the tab becomes visible. Keeps every open client in sync
  // with admin branding changes within ~20s (or immediately on tab focus).
  useEffect(() => {
    const id = setInterval(loadSettings, POLL_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') loadSettings(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadSettings]);

  // Favicon
  useEffect(() => {
    if (site.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = `${API_BASE}/api/admin/branding/${site.favicon}`;
      document.head.appendChild(link);
    }
  }, [site.favicon]);

  // Browser tab title — always suffixed with "Powered by Asset Logic"
  useEffect(() => {
    const name = (site.site_name || 'Asset Logic').trim();
    document.title = `${name} - ${POWERED_BY}`;
  }, [site.site_name]);

  const logoUrl = site.logo ? `${API_BASE}/api/admin/branding/${site.logo}` : '';

  return (
    <SiteContext.Provider value={{ ...site, logoUrl, reload: loadSettings, poweredBy: POWERED_BY }}>
      {children}
    </SiteContext.Provider>
  );
}

export const useSite = () => useContext(SiteContext);
